import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Autocomplete,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconExclamationMark, IconInfoCircle } from '@tabler/icons-react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import API from '@/modules/common/api';
import { BVScheduledFlight, BVScheduledTransport } from '@/modules/common/model';

import { FlightAutocompleteItem, FlightDropdownItem } from './FlightDropdownItem';

const flightNumberSchema = z.object({
  flightNumber: z.string().trim().min(1),
});

type FlightNumberFormData = z.infer<typeof flightNumberSchema>;

interface Props {
  opened: boolean;
  onClose(): void;
  onConfirmWithTransport(transport: BVScheduledFlight): void;
  onConfirmWithFlightNumber(flightNumber: string): void;
  onConfirmNoFlightNumber(): void;
  site: string | number;
  date: string;
}

export const UnfoundTripNumberModal: FC<Props> = ({
  opened,
  onClose,
  onConfirmWithTransport,
  onConfirmWithFlightNumber,
  onConfirmNoFlightNumber,
  site,
  date,
}) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FlightNumberFormData>({
    resolver: zodResolver(flightNumberSchema),
    defaultValues: { flightNumber: '' },
  });

  const flightNumber = watch('flightNumber');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BVScheduledFlight | null>(null);
  const [debouncedQuery] = useDebouncedValue(flightNumber, 300);
  const abortRef = useRef<AbortController | null>(null);

  const isPending = flightNumber.trim() !== debouncedQuery.trim();

  useEffect(() => {
    if (!opened) {
      reset();
      setResult(null);
    }
  }, [opened, reset]);

  useEffect(() => {
    abortRef.current?.abort();

    if (!debouncedQuery.trim()) {
      setResult(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    API.getTripInfos('arrival', site, date, debouncedQuery, controller.signal)
      .then(transport => setResult(transport.type === BVScheduledTransport.Type.Flight ? transport : null))
      .catch(err => {
        if (err.name !== 'AbortError') setResult(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [debouncedQuery, site, date]);

  const handleItemSubmit = (item: FlightAutocompleteItem) => {
    onConfirmWithTransport(item.flight);
    reset();
    setResult(null);
  };

  const onSubmit = ({ flightNumber: value }: FlightNumberFormData) => {
    onConfirmWithFlightNumber(value.toUpperCase());
    reset();
    setResult(null);
  };

  const handleConfirmNoFlightNumber = () => {
    onConfirmNoFlightNumber();
    reset();
    setResult(null);
  };

  const handleClose = () => {
    reset();
    setResult(null);
    onClose();
  };

  const autocompleteData: Array<FlightAutocompleteItem> = result ? [{ value: result.number, flight: result }] : [];

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="lg"
      padding="xl"
      zIndex={20000}
      title={
        <Text c="blue.8" fz="xl" fw="bold">
          {t('booking-trip_unknown_modal_title')}
        </Text>
      }
      radius="lg"
      styles={{
        header: { justifyContent: 'center' },
        body: { paddingLeft: 50, paddingRight: 50 },
        close: { position: 'absolute', top: 10, right: 10 },
      }}
      centered>
      <Stack spacing="xl">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing="md">
            <Text fz="sm" fw={500}>
              {t('booking-flight_not_in_list')}
            </Text>
            <Controller
              name="flightNumber"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  data-cy="unfound-modal-flight-input"
                  placeholder="Ex: AF 6277"
                  data={autocompleteData}
                  itemComponent={FlightDropdownItem}
                  onItemSubmit={handleItemSubmit}
                  filter={() => true}
                  nothingFound={
                    !isPending && !loading && flightNumber.trim() ? t('booking-flight_no_results') : undefined
                  }
                  error={!!errors.flightNumber}
                  rightSection={
                    loading || isPending ? (
                      <Loader size="xs" />
                    ) : field.value ? (
                      <Tooltip label={t('booking-flight_number_not_validated')}>
                        <ThemeIcon color="yellow" variant="outline" radius="xl" size="sm">
                          <IconExclamationMark />
                        </ThemeIcon>
                      </Tooltip>
                    ) : null
                  }
                  {...field}
                />
              )}
            />

            <Button data-cy="unfound-modal-confirm-number" type="submit" radius="xl" fullWidth>
              {flightNumber.trim()
                ? t('booking-flight_use_number', { number: flightNumber.trim().toUpperCase() })
                : t('booking-flight_confirm_number')}
            </Button>
          </Stack>
        </form>

        <Divider label={t('booking-flight_or_divider')} labelPosition="center" />

        <Stack spacing="md">
          <Text fz="sm" fw={500}>
            {t('booking-flight_no_number_question')}
          </Text>
          <Button
            data-cy="confirm-unknown-trip"
            onClick={handleConfirmNoFlightNumber}
            radius="xl"
            variant="outline"
            fullWidth>
            {t('booking-flight_continue_without_number')}
          </Button>
          <Alert icon={<IconInfoCircle />} title={t('booking-flight_notice_title')} color="red" radius="md">
            {t('booking-return-trip-notice')}
          </Alert>
        </Stack>

        <Group position="center" mt="md">
          <Button onClick={handleClose} radius="xl" variant="subtle">
            {t('modal_cancel')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
